import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaBarcode, FaExclamationTriangle, FaFileInvoice, FaShoppingCart, FaTruck } from 'react-icons/fa';
import './PackageIdentifierCards.css';

// The identifiers of the « Informations du colis » section (KI-193): the
// reference (ERP / form), the order number, the invoice number and the
// tracking number — each with where it comes from, always visible (KI-262:
// the team could not tell whether the customer typed a tracking number or
// the AI found it). GET /api/tickets/{id}/context → order_info.provenance,
// order_info.customer_references (what the customer typed in the form) and
// order_info.reference_conflicts (what disagrees with it).

const ORIGIN_LABELS = {
    form: ['messageWindow.fromForm', 'Saisi par le client dans le formulaire'],
    integration: ['messageWindow.fromIntegration', "Transmis par l'intégration"],
    operator: ['messageWindow.setByOperator', 'Saisi par un opérateur'],
    message: ['messageWindow.fromCustomerMessage', 'Lu dans le message du client'],
};

export const valueOrigin = (t, origin) => {
    if (!origin) return null;
    if (!origin.source) return t('messageWindow.originUnknown', 'Origine non enregistrée');
    if (origin.source === 'agent') {
        if (origin.file) return t('messageWindow.readByAgent', "Lu par l'IA dans {{file}}", { file: origin.file });
        if (origin.tool && origin.tool !== 'update_ticket_details') {
            return t('messageWindow.foundByAgentTool', "Trouvé par l'IA (outil {{tool}})", { tool: origin.tool });
        }
        return t('messageWindow.readByAgentNoFile', "Lu par l'IA");
    }
    if (origin.source === 'order_lookup') {
        return origin.connector === 'shop_orders'
            ? t('messageWindow.fromShopOrders', 'Trouvé dans les commandes de la boutique à la création du ticket')
            : t('messageWindow.fromOrderSystem', 'Trouvé par le système de commandes à la création du ticket');
    }
    const label = ORIGIN_LABELS[origin.source];
    return label ? t(label[0], label[1]) : null;
};

const normalized = (value) => String(value || '').replace(/[\s\-_.#/°]/g, '').toUpperCase();
const sameIdentifier = (a, b) => normalized(a) === normalized(b);

const conflictText = (t, conflict) => {
    const by = conflict.by === 'agent'
        ? t('messageWindow.conflictByAgent', "par l'IA (outil {{tool}})", { tool: conflict.tool || '?' })
        : t('messageWindow.conflictByOrderSystem', 'par le système de commandes');
    const vars = { given: conflict.given || '?', found: conflict.found || '?', order: conflict.order || '?', by };
    switch (conflict.reason) {
        case 'order_has_no_tracking':
            return t('messageWindow.conflictOrderHasNoTracking',
                "Le n° de suivi saisi par le client ({{given}}) n'est pas celui de la commande {{order}} : cette commande n'a pas de n° de suivi. Il peut appartenir à une autre commande du client.", vars);
        case 'order_has_other_tracking':
            return t('messageWindow.conflictOrderHasOtherTracking',
                "Le n° de suivi saisi par le client ({{given}}) n'est pas celui de la commande {{order}}, qui porte le n° {{found}}. Le ticket garde le n° du client.", vars);
        case 'other_order':
            return t('messageWindow.conflictOtherOrder',
                "Le client a indiqué la commande {{given}} ; la commande {{found}}, trouvée {{by}}, est une autre commande : elle n'a pas été enregistrée sur le ticket.", vars);
        case 'other_invoice':
            return t('messageWindow.conflictOtherInvoice',
                'Le client a saisi la facture {{given}} ; la commande {{order}} porte la facture {{found}}.', vars);
        case 'given_order_unconfirmed':
            return t('messageWindow.conflictGivenOrderUnconfirmed',
                'Le client a saisi la commande {{given}}, introuvable dans le système de commandes ; la commande {{found}}, trouvée {{by}}, est désormais celle du ticket.', vars);
        default:
            return null;
    }
};

const IdentifierCard = ({ icon, label, value, origin, typed }) => (
    <div className="info-card" title={origin || undefined}>
        <span className="info-label">
            {icon} {label}
        </span>
        <span className="info-value">{value}</span>
        {origin && <span className="info-origin">{origin}</span>}
        {typed && <span className="info-origin info-origin-typed">{typed}</span>}
    </div>
);

const PackageIdentifierCards = ({ orderInfo }) => {
    const { t } = useTranslation();
    const info = orderInfo || {};
    const provenance = info.provenance || {};
    const typedByCustomer = info.customer_references || {};
    const reference = info.reference;
    const orderNumber = info.order_number;
    const invoiceNumber = info.invoice_number;
    const trackingNumber = info.tracking_number;
    // The order number gets its own card when it is not the reference
    // (the agent read another number than the ERP's).
    const orderDiffers = Boolean(orderNumber) && !sameIdentifier(orderNumber, reference);
    // What the customer typed, when the ticket now shows another value.
    const typedLine = (field, shown) => {
        const typed = typedByCustomer[field];
        return typed && !sameIdentifier(typed, shown)
            ? t('messageWindow.typedByCustomer', 'Saisi par le client : {{value}}', { value: typed })
            : null;
    };
    const conflicts = (info.reference_conflicts || [])
        .map((conflict) => conflictText(t, conflict))
        .filter(Boolean);

    return (
        <>
            {reference && (
                <IdentifierCard
                    icon={<FaBarcode />}
                    label={t('messageWindow.reference')}
                    value={reference}
                    origin={valueOrigin(t, provenance.reference)}
                    typed={orderDiffers ? null : typedLine('order_number', reference)}
                />
            )}
            {orderDiffers && (
                <IdentifierCard
                    icon={<FaShoppingCart />}
                    label={t('messageWindow.orderNumber', 'N° de commande')}
                    value={orderNumber}
                    origin={valueOrigin(t, provenance.order_number)}
                    typed={typedLine('order_number', orderNumber)}
                />
            )}
            {invoiceNumber && (
                <IdentifierCard
                    icon={<FaFileInvoice />}
                    label={t('messageWindow.invoiceNumber', 'N° de facture')}
                    value={invoiceNumber}
                    origin={valueOrigin(t, provenance.invoice_number)}
                    typed={typedLine('invoice_number', invoiceNumber)}
                />
            )}
            {trackingNumber && (
                <IdentifierCard
                    icon={<FaTruck />}
                    label={t('messageWindow.trackingNumber', 'Numéro de suivi')}
                    value={trackingNumber}
                    origin={valueOrigin(t, provenance.tracking_number)}
                    typed={typedLine('tracking_number', trackingNumber)}
                />
            )}
            {conflicts.length > 0 && (
                <div className="info-card info-card-wide reference-conflicts" role="note">
                    <span className="info-label">
                        <FaExclamationTriangle /> {t('messageWindow.referenceConflicts', 'Références en désaccord')}
                    </span>
                    {conflicts.map((text) => (
                        <span key={text} className="reference-conflict">{text}</span>
                    ))}
                </div>
            )}
        </>
    );
};

export default PackageIdentifierCards;
